import React, { useEffect, useRef, useState } from 'react';

export const PawRicochet: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [score, setScore] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [ballsCount, setBallsCount] = useState(10);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Game Config
        let animationFrameId: number;
        let isAiming = false;
        let isFiring = false;
        let aimAngle = -Math.PI / 2;
        let balls: { x: number, y: number, dx: number, dy: number, active: boolean }[] = [];
        let blocks: { x: number, y: number, hp: number, w: number, h: number, color: string }[] = [];
        let particles: { x: number, y: number, life: number, color: string, dx: number, dy: number }[] = [];

        const BALL_RADIUS = 6;
        const BLOCK_SIZE = canvas.width / 8;
        const BLOCK_GAP = 4;
        const SPEED = 12;

        let launcherX = canvas.width / 2;
        let baseBalls = 10;
        let returnedBalls = 0;
        let round = 1;

        // Init Blocks
        const addRow = () => {
            for (let i = 0; i < 7; i++) {
                if (Math.random() > 0.5) {
                    blocks.push({
                        x: i * (BLOCK_SIZE + BLOCK_GAP) + BLOCK_GAP,
                        y: 60, // Start below hud
                        hp: round, // HP equals round number
                        w: BLOCK_SIZE,
                        h: BLOCK_SIZE / 1.5,
                        color: `hsl(${Math.random() * 360}, 70%, 60%)`
                    });
                }
            }
        };

        const resetGame = () => {
            blocks = [];
            round = 1;
            baseBalls = 10;
            setBallsCount(10);
            setScore(0);
            setGameOver(false);
            launcherX = canvas.width / 2;
            addRow();
            loop();
        };

        const fire = () => {
            if (isFiring) return;
            isFiring = true;
            returnedBalls = 0;
            let count = 0;

            const interval = setInterval(() => {
                balls.push({
                    x: launcherX,
                    y: canvas.height - 20,
                    dx: Math.cos(aimAngle) * SPEED,
                    dy: Math.sin(aimAngle) * SPEED,
                    active: true
                });
                count++;
                setBallsCount(baseBalls - count);
                if (count >= baseBalls) clearInterval(interval);
            }, 100);
        };

        const update = () => {
            // Balls
            let activeBalls = false;
            balls.forEach(ball => {
                if (!ball.active) return;
                activeBalls = true;

                ball.x += ball.dx;
                ball.y += ball.dy;

                // Walls
                if (ball.x < 0 || ball.x > canvas.width) ball.dx *= -1;
                if (ball.y < 0) ball.dy *= -1;

                // Floor (Return)
                if (ball.y > canvas.height) {
                    ball.active = false;
                    returnedBalls++;
                    if (returnedBalls === baseBalls) {
                        isFiring = false;
                        balls = [];
                        setBallsCount(baseBalls);
                        // Next Round
                        round++;
                        setScore(round);
                        moveBlocks();
                    }
                    if (returnedBalls === 1) {
                        // First ball sets new launcher position? Simplified: Stick to center or create catch logic. 
                        // For simplicity, let's keep launcher static or use this x.
                        launcherX = ball.x; // Set launcher to where first ball landed
                        if (launcherX < 20) launcherX = 20;
                        if (launcherX > canvas.width - 20) launcherX = canvas.width - 20;
                    }
                }

                // Blocks Collision
                blocks.forEach((block, i) => {
                    if (
                        ball.x > block.x &&
                        ball.x < block.x + block.w &&
                        ball.y > block.y &&
                        ball.y < block.y + block.h
                    ) {
                        // Hit
                        // Determine side? Simplified: just bounce
                        // Simple bounce logic based on overlap
                        const overlapX = Math.min(Math.abs(ball.x - block.x), Math.abs(ball.x - (block.x + block.w)));
                        const overlapY = Math.min(Math.abs(ball.y - block.y), Math.abs(ball.y - (block.y + block.h)));

                        if (overlapX < overlapY) ball.dx *= -1;
                        else ball.dy *= -1;

                        block.hp--;
                        // Particles
                        for (let p = 0; p < 3; p++) particles.push({ x: ball.x, y: ball.y, life: 1, color: block.color, dx: (Math.random() - 0.5) * 5, dy: (Math.random() - 0.5) * 5 });

                        // Pass through a bit to avoid stuck
                    }
                });
            });

            // Clean Blocks
            for (let i = blocks.length - 1; i >= 0; i--) {
                if (blocks[i].hp <= 0) blocks.splice(i, 1);
            }

            // Particles
            for (let i = particles.length - 1; i >= 0; i--) {
                const p = particles[i];
                p.x += p.dx;
                p.y += p.dy;
                p.life -= 0.05;
                if (p.life <= 0) particles.splice(i, 1);
            }
        };

        const moveBlocks = () => {
            blocks.forEach(b => b.y += BLOCK_SIZE);
            // Check Game Over
            if (blocks.some(b => b.y + b.h > canvas.height - 50)) {
                setGameOver(true);
            } else {
                addRow();
            }
        };

        const draw = () => {
            // BG
            ctx.fillStyle = '#111827';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Blocks
            blocks.forEach(b => {
                ctx.fillStyle = b.color;
                ctx.fillRect(b.x, b.y, b.w, b.h);
                // Text
                ctx.fillStyle = 'rgba(0,0,0,0.5)';
                ctx.font = 'bold 12px sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(b.hp.toString(), b.x + b.w / 2, b.y + b.h / 2);
            });

            // Balls
            ctx.fillStyle = '#fff';
            balls.forEach(b => {
                if (b.active) {
                    ctx.beginPath();
                    ctx.arc(b.x, b.y, BALL_RADIUS, 0, Math.PI * 2);
                    ctx.fill();
                }
            });

            // Launcher
            if (!isFiring) {
                ctx.beginPath();
                ctx.arc(launcherX, canvas.height - 20, 10, 0, Math.PI * 2);
                ctx.fillStyle = '#fff';
                ctx.fill();

                // Aim Line
                if (isAiming) {
                    ctx.beginPath();
                    ctx.moveTo(launcherX, canvas.height - 20);
                    ctx.lineTo(launcherX + Math.cos(aimAngle) * 100, canvas.height - 20 + Math.sin(aimAngle) * 100);
                    ctx.strokeStyle = 'rgba(255,255,255,0.3)';
                    ctx.setLineDash([5, 5]);
                    ctx.stroke();
                    ctx.setLineDash([]);
                }
            }

            // Particles
            particles.forEach(p => {
                ctx.fillStyle = p.color;
                ctx.globalAlpha = p.life;
                ctx.fillRect(p.x, p.y, 4, 4);
                ctx.globalAlpha = 1;
            });
        };

        const loop = () => {
            if (!gameOver) {
                update();
                draw();
                animationFrameId = requestAnimationFrame(loop);
            } else {
                // Game Over Screen Draw
                ctx.fillStyle = 'rgba(0,0,0,0.8)';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.fillStyle = '#fff';
                ctx.font = '30px sans-serif';
                ctx.fillText("GAME OVER", canvas.width / 2, canvas.height / 2);
            }
        };

        // Inputs
        const handleStart = (e: MouseEvent | TouchEvent) => {
            if (isFiring || gameOver) return;
            isAiming = true;
        };
        const handleMove = (e: MouseEvent | TouchEvent) => {
            if (!isAiming) return;
            let clientX = 0, clientY = 0;
            if (e instanceof MouseEvent) { clientX = e.clientX; clientY = e.clientY; }
            else { clientX = e.touches[0].clientX; clientY = e.touches[0].clientY; }

            const dx = clientX - launcherX;
            const dy = clientY - (canvas.height - 20);
            let angle = Math.atan2(dy, dx);
            if (angle > -0.2) angle = -0.2; // Limit floor aiming
            if (angle < -Math.PI + 0.2) angle = -Math.PI + 0.2;
            aimAngle = angle;
        };
        const handleEnd = () => {
            if (isAiming) {
                isAiming = false;
                fire();
            }
        };

        canvas.addEventListener('mousedown', handleStart);
        canvas.addEventListener('mousemove', handleMove);
        canvas.addEventListener('mouseup', handleEnd);
        canvas.addEventListener('touchstart', handleStart);
        canvas.addEventListener('touchmove', handleMove);
        canvas.addEventListener('touchend', handleEnd);

        // Resize
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        resetGame();

        return () => {
            canvas.removeEventListener('mousedown', handleStart);
            canvas.removeEventListener('mousemove', handleMove);
            canvas.removeEventListener('mouseup', handleEnd);
            cancelAnimationFrame(animationFrameId);
        };

    }, []);

    return (
        <div className="relative w-full h-full bg-slate-900 overflow-hidden">
            <canvas ref={canvasRef} className="block w-full h-full touch-none" />
            <div className="absolute top-4 left-4 text-white font-black text-2xl drop-shadow-md pointer-events-none">
                ROUND: {Math.max(1, score)}
            </div>
            <div className="absolute bottom-4 left-4 text-white font-mono text-xs pointer-events-none">
                BOLAS: {ballsCount}
            </div>
            {gameOver && <button onClick={() => window.location.reload()} className="absolute top-2/3 left-1/2 -translate-x-1/2 bg-white text-black p-4 rounded-xl font-bold">REINTENTAR</button>}
        </div>
    );
};
